import Styles from './styles.module.css';
import React, { useRef, useState } from 'react';
import { getFirestore, addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { deleteObject, getStorage, ref, uploadBytes } from 'firebase/storage';
import { v4 as uuid } from 'uuid';
import { IoPersonOutline } from 'react-icons/io5';

export type StaffEditing = {
  id: string;
  docId: string;
  name: string;
  position: string;
  message: string;
  instagramId: string;
  tiktokId: string;
};

type StaffProfileMakeProps = {
  companyId: string;
  setMake: (open: boolean) => void;
  // 渡したときは、そのスタッフの編集(役職・SNSなどを後から変えられる)。写真は変えたときだけ差し替える。
  editing?: StaffEditing | null;
};

const StaffProfileMake: React.FC<StaffProfileMakeProps> = ({ companyId, setMake, editing = null }) => {

  const db = getFirestore();

  const bodyRef = useRef<HTMLDivElement | null>(null);

  const [ name, setName ] = useState<string>(editing?.name ?? '');
  const [ position, setPosition ] = useState<string>(editing?.position ?? '');
  const [ message, setMessage ] = useState<string>(editing?.message ?? '');
  const [ instagramId, setInstagramId ] = useState<string>(editing?.instagramId ?? '');
  const [ tiktokId, setTiktokId ] = useState<string>(editing?.tiktokId ?? '');
  const [ imagePreview, setImagePreview ] = useState<string>(
    editing?.docId ? `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/staffProfiles%2F${editing.docId}?alt=media` : '',
  );
  const [ imageFile, setImageFile ] = useState<File | null>(null);

  const [ errorImage, setErrorImage ] = useState(false);
  const [ errorName, setErrorName ] = useState(false);
  const [ uploading, setUploading ] = useState(false);

  // 保存時のバリデーション結果を1件だけ上部に表示する
  const [ errorMessage, setErrorMessage ] = useState<string>('');

  const showError = (message: string) => {
    setErrorMessage(message);
    bodyRef.current?.scrollTo({ top: 0 });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImagePreview(URL.createObjectURL(e.target.files[0]));
      setImageFile(e.target.files[0]);
      setErrorImage(false);
    }
  };

  async function save() {

    setErrorImage(false);
    setErrorName(false);
    setErrorMessage('');

    if (!imageFile && !editing) {
      setErrorImage(true);
      showError('プロフィール画像を設定してください。');
    } else if (name === '') {
      setErrorName(true);
      showError('名前を入力してください。');
    } else {
      try {
        setUploading(true);
        const storage = getStorage();

        if (editing) {
          // 写真を変えたときは新しい名前で保存し直す(同じ名前で上書きすると、ブラウザやアプリに
          // 古い写真が残って見えるため)。作品撮りのスタッフ紐づけはドキュメントIDなので影響しない。
          let docId = editing.docId;
          if (imageFile) {
            docId = uuid();
            await uploadBytes(ref(storage, `staffProfiles/${docId}`), imageFile);
          }
          await updateDoc(doc(db, 'staffProfiles', editing.id), {
            docId, name, position, message, instagramId, tiktokId,
            updated_at: new Date(),
          });
          if (imageFile && editing.docId) {
            // 古い写真(と自動で作られる縮小版)は消す。消せなくても保存は終わっているので続ける。
            await Promise.all([
              deleteObject(ref(storage, `staffProfiles/${editing.docId}`)),
              deleteObject(ref(storage, `staffProfiles/resize_images/${editing.docId}_500x500`)),
            ].map((p) => p.catch(() => undefined)));
          }
          setMake(false);
          return;
        }

        if (!imageFile) return;
        const docId = uuid();
        const imageRef = ref(storage, `staffProfiles/${docId}`);
        await uploadBytes(imageRef, imageFile);

        await addDoc(collection(db, 'staffProfiles'), {
          companyId: companyId,
          docId: docId,
          name: name,
          position: position,
          message: message,
          instagramId: instagramId,
          tiktokId: tiktokId,
          created_at: new Date(),
        });

        setMake(false);
      } catch (error) {
        console.error('エラーが発生しました', error);
        setUploading(false);
      }
    }

  }

  const inputClass = (hasError: boolean) =>
    hasError ? `${Styles.input} ${Styles.input_error}` : Styles.input;

  return (
    <div className={Styles.overlay} role='dialog' aria-modal='true'>
      <div className={Styles.scrim} onClick={() => !uploading && setMake(false)} />
      <div className={Styles.card}>

        <div className={Styles.header}>
          <h3 className={Styles.headerTitle}>{editing ? 'スタッフの編集' : 'スタッフの追加'}</h3>
          <button type='button' className={Styles.headerBtn} onClick={() => save()} disabled={uploading}>
            {uploading ? '保存中…' : editing ? '保存' : '追加'}
          </button>
        </div>

        <div className={Styles.body} ref={bodyRef}>

          {errorMessage && (
            <div className={Styles.errorBox} role='alert'>
              <p className={Styles.errorBox_label}>エラーメッセージ：</p>
              <p className={Styles.errorBox_text}>{errorMessage}</p>
            </div>
          )}

          {/* プロフィール画像 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>プロフィール画像<span className={Styles.req}>※</span></span>
            </div>
            <label className={errorImage ? `${Styles.imageBox} ${Styles.imageBox_error}` : Styles.imageBox}>
              {imagePreview ? (
                <img className={Styles.imagePreview} src={imagePreview} alt='プロフィール画像' />
              ) : (
                <div className={Styles.imagePlaceholder}>
                  <IoPersonOutline className={Styles.imagePlaceholderIcon} />
                  <span>写真を追加</span>
                </div>
              )}
              <input type='file' accept='image/*' onChange={handleImageChange} disabled={uploading} className={Styles.hiddenInput} />
            </label>
          </div>

          {/* 名前 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>名前<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{name.length} / 10</span>
            </div>
            <input
              className={inputClass(errorName)}
              placeholder='山田 花子'
              maxLength={10}
              type='text'
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={uploading}
            />
          </div>

          {/* 役職 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>役職<span className={Styles.optional}>任意</span></span>
              <span className={Styles.count}>{position.length} / 15</span>
            </div>
            <input
              className={Styles.input}
              placeholder='スタイリスト・アシスタント など'
              maxLength={15}
              type='text'
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              disabled={uploading}
            />
          </div>

          {/* 一言 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>一言<span className={Styles.optional}>任意</span></span>
              <span className={Styles.count}>{message.length} / 50</span>
            </div>
            <textarea
              className={`${Styles.input} ${Styles.textarea}`}
              placeholder='一言メッセージを入力してください'
              maxLength={50}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={uploading}
            />
          </div>

          {/* Instagram ID */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>Instagram ID<span className={Styles.optional}>任意</span></span>
              <span className={Styles.count}>{instagramId.length} / 50</span>
            </div>
            <input
              className={Styles.input}
              placeholder='username（@は不要）'
              maxLength={50}
              type='text'
              value={instagramId}
              onChange={(e) => setInstagramId(e.target.value)}
              disabled={uploading}
            />
          </div>

          {/* TikTok ID */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>TikTok ID<span className={Styles.optional}>任意</span></span>
              <span className={Styles.count}>{tiktokId.length} / 50</span>
            </div>
            <input
              className={Styles.input}
              placeholder='username（@は不要）'
              maxLength={50}
              type='text'
              value={tiktokId}
              onChange={(e) => setTiktokId(e.target.value)}
              disabled={uploading}
            />
          </div>

        </div>
      </div>
    </div>
  );
}

export default StaffProfileMake;
